// pages/AttemptPlayer/components/widgets/realtimeTranscriber.ts
//
// Instant voice feedback. While the student speaks, the mic is streamed to the
// OpenAI Realtime API (transcription session), so the transcript is ready about
// half a second after they press stop — instead of uploading the whole clip to
// Whisper afterwards (several seconds). Same code runs on the website and in the
// app WebView.
//
// This is only a FAST PATH: nothing here ever throws. Any problem (no token, socket
// error, no audio, timeout, empty transcript) resolves to null and VoiceWidget
// carries on with the normal Whisper upload exactly as before.

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const REALTIME_URL = "wss://api.openai.com/v1/realtime?intent=transcription";
const TARGET_RATE = 24000; // the transcription session only accepts 24 kHz PCM16
const MAX_PENDING_CHUNKS = 600; // ~60 s of audio buffered before the socket opens

export type RealtimeTranscription = {
  /** Stop capturing, commit the audio and wait for the transcript (null = use Whisper). */
  finish: (timeoutMs: number) => Promise<string | null>;
  /** Drop everything (unmount / failed start). */
  abort: () => void;
};

// Plain fetch on purpose: WebService logs the user out on a 401, and this
// optional fast path must never be able to do that.
export const postJson = async (action: string, body: any): Promise<any> => {
  const res = await fetch(`${BASE_URL}${action}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: "Bearer " + localStorage.getItem("access_token"),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${action} ${res.status}`);
  return res.json();
};

// Float32 samples at `fromRate` -> 24 kHz little-endian PCM16, base64.
export const toPcm16Base64 =(input: Float32Array, fromRate: number): string => {
  const ratio = fromRate / TARGET_RATE;
  const outLen = Math.floor(input.length / ratio);
  const bytes = new Uint8Array(outLen * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const s = input[i0] + (input[i1] - input[i0]) * (pos - i0); // linear
    const clamped = Math.max(-1, Math.min(1, s));
    view.setInt16(i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  }
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + 0x8000) as unknown as number[],
    );
  }
  return btoa(bin);
};

export const startRealtimeTranscription = (
  stream: MediaStream,
  language: string,
): RealtimeTranscription => {
  let ws: WebSocket | null = null;
  let failed = false;
  let finishing = false;
  let committed = false;
  let sentAudio = false;
  let stoppedAt = 0;
  const pending: string[] = [];
  let resolveTranscript: ((t: string | null) => void) | null = null;
  let transcript: string | null | undefined; // undefined = not arrived yet

  const settle = (t: string | null) => {
    if (transcript !== undefined) return;
    transcript = t;
    resolveTranscript?.(t);
  };
  const fail = (reason: string) => {
    if (!failed && transcript === undefined) {
      console.warn(`[voice] live transcription unavailable (${reason}) — using Whisper`);
    }
    failed = true;
    settle(null);
  };

  // ---- audio capture (ScriptProcessor: works in every browser + WebView) ----
  let ctx: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let processor: ScriptProcessorNode | null = null;
  let mute: GainNode | null = null;

  const send = (msg: object) => {
    try {
      ws?.send(JSON.stringify(msg));
    } catch {
      fail("socket send failed");
    }
  };

  const pushAudio = (b64: string) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      send({ type: "input_audio_buffer.append", audio: b64 });
      sentAudio = true;
    } else if (pending.length < MAX_PENDING_CHUNKS) {
      pending.push(b64);
    }
  };

  const commitIfReady = () => {
    if (committed || !finishing || !ws || ws.readyState !== WebSocket.OPEN) return;
    if (!sentAudio) return fail("no audio captured"); // e.g. suspended AudioContext
    committed = true;
    send({ type: "input_audio_buffer.commit" });
  };

  const stopCapture = () => {
    try {
      if (processor) processor.onaudioprocess = null;
      source?.disconnect();
      processor?.disconnect();
      mute?.disconnect();
      ctx?.close?.();
    } catch {}
    ctx = null;
    source = null;
    processor = null;
    mute = null;
  };

  const closeSocket = () => {
    try {
      ws?.close();
    } catch {}
    ws = null;
  };

  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx || !(window as any).WebSocket) throw new Error("unsupported browser");
    // Run the context at the MIC's own rate: Firefox refuses to connect a mic
    // stream to a context with a different sample rate (e.g. 44.1k mic, 48k
    // output). We resample to 24 kHz ourselves anyway.
    const micRate = stream.getAudioTracks()[0]?.getSettings?.().sampleRate;
    try {
      ctx = (micRate ? new Ctx({ sampleRate: micRate }) : new Ctx()) as AudioContext;
      source = ctx.createMediaStreamSource(stream);
    } catch {
      try {
        ctx?.close?.();
      } catch {}
      ctx = new Ctx() as AudioContext;
      source = ctx.createMediaStreamSource(stream);
    }
    ctx.resume?.().catch(() => {});
    processor = ctx.createScriptProcessor(4096, 1, 1);
    mute = ctx.createGain();
    mute.gain.value = 0; // processor must reach the destination to run; keep it silent
    const rate = ctx.sampleRate;
    processor.onaudioprocess = (e) => {
      if (failed || committed) return;
      pushAudio(toPcm16Base64(e.inputBuffer.getChannelData(0), rate));
    };
    source.connect(processor);
    processor.connect(mute);
    mute.connect(ctx.destination);
  } catch (e: any) {
    fail(`audio capture: ${e?.message || e}`);
  }

  // ---- token + socket (in parallel with the student speaking) ----
  if (!failed) {
    postJson("/core/realtime-token", { language })
      .then((res) => {
        if (failed) return;
        if (!res?.value) return fail("no token");
        ws = new WebSocket(REALTIME_URL, [
          "realtime",
          "openai-insecure-api-key." + res.value,
        ]);
        ws.onopen = () => {
          while (pending.length) {
            send({ type: "input_audio_buffer.append", audio: pending.shift() });
            sentAudio = true;
          }
          commitIfReady();
        };
        ws.onmessage = (ev) => {
          let msg: any;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (msg?.type === "error") {
            return fail(`openai error: ${msg?.error?.message || msg?.error?.code || "?"}`);
          }
          if (msg?.type === "conversation.item.input_audio_transcription.completed") {
            const t = String(msg.transcript || "").trim();
            if (!t) return fail("empty transcript"); // let Whisper have a go
            console.info(
              `[voice] live transcript in ${Math.round(performance.now() - stoppedAt)} ms after stop`,
            );
            settle(t);
            closeSocket();
          }
        };
        ws.onerror = () => fail("socket error");
        ws.onclose = (ev) => {
          if (transcript === undefined) fail(`socket closed (${ev.code})`);
        };
      })
      .catch((e) => fail(`token request: ${e?.message || e}`));
  }

  return {
    finish: (timeoutMs) => {
      finishing = true;
      stoppedAt = performance.now();
      stopCapture();
      if (transcript !== undefined) {
        closeSocket();
        return Promise.resolve(transcript);
      }
      commitIfReady();
      return new Promise<string | null>((resolve) => {
        const timer = window.setTimeout(() => {
          fail(`no transcript within ${timeoutMs} ms`);
        }, timeoutMs);
        resolveTranscript = (t) => {
          window.clearTimeout(timer);
          closeSocket();
          resolve(t);
        };
        if (transcript !== undefined) resolveTranscript(transcript);
      });
    },
    abort: () => {
      failed = true;
      stopCapture();
      closeSocket();
      settle(null);
    },
  };
};
